import { z } from 'zod';
import { ArtifactSchema } from './artifact.js';
import { AgentVerdictSchema } from './verdict.js';

/**
 * @aiPurpose one persisted turn in a conversation. `role: 'user'` carries the
 *   original question; `role: 'assistant'` carries the answer prose plus any
 *   artifacts and verdict. `artifacts` is empty for user turns. `verdict` is
 *   present only on assistant turns that completed reconciliation.
 * @aiPii `text` may contain user-supplied question text or agent-generated
 *   answers that reference business figures — treat as internal-confidential.
 */
export const AgentMessageSchema = z
  .object({
    id: z.string().uuid(),
    role: z.enum(['user', 'assistant']),
    text: z.string(),
    artifacts: z.array(ArtifactSchema),
    verdict: AgentVerdictSchema.optional(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type AgentMessage = z.infer<typeof AgentMessageSchema>;

/**
 * @aiPurpose full conversation wire row returned by
 *   `GET /api/agent/conversations/:id`. The `title` is derived from the
 *   first user turn (truncated). `messages` are ordered ascending by
 *   `createdAt`.
 * @aiInvariant `messages` always has at least one entry (the initial user
 *   turn). `updatedAt` equals the `createdAt` of the most recent message.
 * @aiPii same as `AgentMessageSchema` — treat as internal-confidential.
 */
export const AgentConversationSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    messages: z.array(AgentMessageSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export type AgentConversation = z.infer<typeof AgentConversationSchema>;
